/**
 * Demo data for the landing-page playground. One coherent topic — the science
 * of learning — so the demo content itself argues for the product's pedagogy:
 * visitors practice retrieval while reading about why retrieval works.
 *
 * All four activities run fully client-side (local checkers / MCQ-only), the
 * same way InteractiveEditorModal previews unsaved drafts.
 */

import type { Flashcard } from "@/components/interactives/FlashcardDeck";
import type { MCQuestion } from "@/components/interactives/KnowledgeCheck";
import type { MatchEntry } from "@/components/interactives/MatchingActivity";
import type { SequencingStep } from "@/components/interactives/SequencingActivity";

export const DEMO_FLASHCARDS: Flashcard[] = [
  {
    front: "What is retrieval practice?",
    back: "Recalling information from memory. The act of remembering strengthens the memory itself — more than re-reading ever does.",
  },
  {
    front: "What is the spacing effect?",
    back: "Learning sticks better when practice is spread across days instead of crammed into one session.",
  },
  {
    front: "What is the testing effect?",
    back: "Low-stakes quizzing improves long-term retention more than studying the material again.",
  },
  {
    front: "Why does interleaving feel harder?",
    back: "Mixing related topics forces you to tell problems apart instead of running on autopilot — the difficulty is what builds the skill.",
  },
];

// Pairs are correct when left.id === right.id; the playground shuffles the
// right column and supplies a local checkMatch comparing ids.
export const DEMO_MATCH_PAIRS: { left: string; right: string }[] = [
  { left: "Encoding", right: "Getting information into memory in the first place" },
  { left: "Consolidation", right: "Stabilizing a new memory, largely during sleep" },
  { left: "Retrieval", right: "Pulling information back out when you need it" },
  { left: "Forgetting curve", right: "How quickly memories fade without review" },
];

export const DEMO_MATCH_LEFTS: MatchEntry[] = DEMO_MATCH_PAIRS.map((p, i) => ({
  id: String(i),
  text: p.left,
}));

export const DEMO_MATCH_RIGHTS: MatchEntry[] = DEMO_MATCH_PAIRS.map((p, i) => ({
  id: String(i),
  text: p.right,
}));

// Correct order = array order; the playground shuffles for display and
// supplies a local check comparing against this sequence.
export const DEMO_SEQUENCE: SequencingStep[] = [
  { id: "attend", text: "Attend — notice the new idea instead of skimming past it" },
  { id: "encode", text: "Encode — connect it to something you already know" },
  { id: "consolidate", text: "Consolidate — let sleep stabilize the memory" },
  { id: "retrieve", text: "Retrieve — recall it from memory, without peeking" },
  { id: "space", text: "Space — review again just as it starts to fade" },
];

// Explorable timeline (chronological order; the component spaces nodes to
// scale because these labels parse as years).
export const DEMO_TIMELINE = [
  {
    label: "1885",
    text: "Ebbinghaus charts the forgetting curve",
    detail:
      "Testing his own memory with nonsense syllables, Hermann Ebbinghaus showed that memories decay on a predictable curve — steep at first, then flattening.\n\nEvery spaced-review schedule since is an answer to this curve.",
  },
  {
    label: "1917",
    text: "Gates shows recitation beats rereading",
    detail:
      "Arthur Gates found that students who spent most of their study time reciting from memory outperformed those who only reread — the first classroom evidence for retrieval practice.",
  },
  {
    label: "1939",
    text: "Spitzer takes spaced testing into real classrooms",
    detail:
      "Herbert Spitzer tested over 3,600 Iowa sixth-graders and showed that an early quiz slowed forgetting for weeks — testing as a learning event, not just a measurement.",
  },
  {
    label: "2006",
    text: "Roediger & Karpicke revive the testing effect",
    detail:
      "Their lab and classroom studies made retrieval practice the backbone of modern learning science: recalling material strengthens it more than restudying it.",
    link: { url: "https://doi.org/10.1111/j.1467-9280.2006.01693.x", text: "The 2006 paper" },
  },
];

export const DEMO_WORKED = {
  prompt:
    "An exam is 16 days away. Plan reviews of today's lesson using spaced retrieval.",
  steps: [
    "Start with a self-quiz today, right after studying — recalling it once from memory is the strongest first encoding.",
    "Space the reviews by roughly doubling the gap: days $1, 2, 4, 8, 16$ — each review lands just as the memory starts to fade.",
    "Make every review retrieval, not rereading: close the notes, answer from memory, then check.",
    "The last review falls on day $16$ — exam day becomes just another successful recall.",
  ],
};

/**
 * Custom sandboxed widget for the "build your own" showcase — the SAME
 * strings are shown in the editor pane and executed in the JsInteractive
 * sandbox, so what visitors read is exactly what runs. Continues the hero's
 * ASTR 101 tides lesson.
 */
export const DEMO_WIDGET = {
  html: `<p class="hint">Drag the Moon around Earth — the two
tidal bulges follow it.</p>
<input id="orbit" type="range" min="0" max="360"
       value="35" aria-label="Moon position (degrees)">
<canvas id="sky" width="640" height="240"></canvas>`,
  css: `.hint { font: 14px system-ui; color: #445; margin: 0 0 6px; }
input { width: 100%; accent-color: #0374b5; }
canvas { width: 100%; height: auto; display: block; }`,
  js: `var ctx = sky.getContext("2d");

function draw() {
  var a = orbit.value * Math.PI / 180;
  ctx.clearRect(0, 0, 640, 240);

  // Ocean: an ellipse whose long axis points at the Moon —
  // one bulge from gravity, one from inertia
  ctx.save();
  ctx.translate(320, 120);
  ctx.rotate(a);
  ctx.fillStyle = "#9ed4f2";
  ctx.beginPath();
  ctx.ellipse(0, 0, 82, 56, 0, 0, 7);
  ctx.fill();
  ctx.restore();

  // Earth
  ctx.fillStyle = "#14608f";
  ctx.beginPath();
  ctx.arc(320, 120, 50, 0, 7);
  ctx.fill();

  // Moon
  ctx.fillStyle = "#cfd8dd";
  ctx.beginPath();
  ctx.arc(320 + Math.cos(a) * 240, 120 + Math.sin(a) * 92, 13, 0, 7);
  ctx.fill();
}

orbit.addEventListener("input", draw);
draw();`,
};

export const DEMO_QUESTIONS: MCQuestion[] = [
  {
    kind: "single",
    prompt: "A student has one week before an exam. Which plan does the research favor?",
    options: [
      "Reread the chapter twice the night before",
      "Three short self-quizzes spread across the week",
      "Highlight the chapter, then recopy the notes",
      "One long review session mid-week",
    ],
    correct: 1,
    explanation:
      "Spaced retrieval practice beats rereading and massed review — remembering is what strengthens memory.",
  },
  {
    kind: "multi",
    prompt: "Which of these count as retrieval practice? Select all that apply.",
    options: [
      "Answering flashcards from memory",
      "Rereading a highlighted chapter",
      "Writing down what you remember before checking your notes",
      "Watching the lecture recording again",
    ],
    correct: [0, 2],
    explanation:
      "If the answer comes out of your head rather than off the page, it's retrieval. Rereading and rewatching feel productive but leave weaker traces.",
  },
];
